import React, { useState, useEffect, useRef } from 'react';
import styles from './Sudoku.module.css';
import successSound from '../assets/sounds/success.mp3';
import failSound from '../assets/sounds/fail.mp3';
import { useLanguage } from '../contexts/LanguageContext';

const successAudio = new Audio(successSound);
const failAudio = new Audio(failSound);

function Sudoku({ difficulty, onWrongAnswer, lives, cellsToShow, onUserInput, hasUserInput, onGameOver, onGameClear, resetBoard }) {
  const [board, setBoard] = useState(Array(9).fill().map(() => Array(9).fill(0)));
  const [initialBoard, setInitialBoard] = useState(Array(9).fill().map(() => Array(9).fill(0)));
  const [solution, setSolution] = useState(Array(9).fill().map(() => Array(9).fill(0)));
  const [selectedCell, setSelectedCell] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [wrongCells, setWrongCells] = useState(new Set());
  const [hoveredNumber, setHoveredNumber] = useState(null);
  const [completedSections, setCompletedSections] = useState([]);
  const [completedNumbers, setCompletedNumbers] = useState(new Set());
  const [isMemoMode, setIsMemoMode] = useState(false);
  const [memos, setMemos] = useState(() => {
    return Array(9).fill().map(() =>
      Array(9).fill().map(() => new Set())
    );
  });
  const { t } = useLanguage();

  const timeoutIdsRef = useRef(new Set());
  const trackedSetTimeout = (callback, delay) => {
    const id = setTimeout(() => {
      timeoutIdsRef.current.delete(id);
      callback();
    }, delay);
    timeoutIdsRef.current.add(id);
    return id;
  };

  useEffect(() => {
    const ids = timeoutIdsRef.current;
    return () => {
      ids.forEach(id => clearTimeout(id));
      ids.clear();
    };
  }, []);

  const generateSudoku = () => {
    const newBoard = Array(9).fill().map(() => Array(9).fill(0));
    
    // 첫 줄을 랜덤하게 생성
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    for (let i = 8; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [numbers[i], numbers[j]] = [numbers[j], numbers[i]]; // 배열 섞기
    }
    newBoard[0] = [...numbers];
    
    const solvedBoard = solveSudoku([...newBoard.map(row => [...row])]);
    
    if (!solvedBoard) {
      // 해결할 수 없는 경우 다시 시도
      return generateSudoku();
    }

    const finalBoard = [...solvedBoard].map(row => [...row]);
    let totalCells = 81;
    let cellsToHide = totalCells - cellsToShow;

    while (cellsToHide > 0) {
      const row = Math.floor(Math.random() * 9);
      const col = Math.floor(Math.random() * 9);
      if (finalBoard[row][col] !== 0) {
        finalBoard[row][col] = 0;
        cellsToHide--;
      }
    }

    setSolution(solvedBoard);
    setInitialBoard(finalBoard.map(row => [...row]));
    return finalBoard;
  };

  const solveSudoku = (board) => {
    const find_empty = (board) => {
      for (let i = 0; i < 9; i++) {
        for (let j = 0; j < 9; j++) {
          if (board[i][j] === 0) return [i, j];
        }
      }
      return null;
    };

    const valid = (board, num, pos) => {
      // 행 체크
      for (let x = 0; x < 9; x++) {
        if (board[pos[0]][x] === num && pos[1] !== x) return false;
      }

      // 열 체크
      for (let x = 0; x < 9; x++) {
        if (board[x][pos[1]] === num && pos[0] !== x) return false;
      }

      // 3x3 박스 체크
      const box_x = Math.floor(pos[1] / 3);
      const box_y = Math.floor(pos[0] / 3);

      for (let i = box_y * 3; i < box_y * 3 + 3; i++) {
        for (let j = box_x * 3; j < box_x * 3 + 3; j++) {
          if (board[i][j] === num && (i !== pos[0] || j !== pos[1])) return false;
        }
      }

      return true;
    };

    const empty = find_empty(board);
    if (!empty) return board;

    const [row, col] = empty;

    // 숫자 순서를 랜덤화
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    for (let i = numbers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
    }

    for (let num of numbers) {
      if (valid(board, num, [row, col])) {
        board[row][col] = num;

        if (solveSudoku(board)) return board;

        board[row][col] = 0;
      }
    }

    return false;
  };

  useEffect(() => {
    const newBoard = generateSudoku();
    setBoard(newBoard);
    setGameOver(false);
    setWrongCells(new Set());
    setSelectedCell(null);
  }, [difficulty]);

  useEffect(() => {
    if (!hasUserInput) {
      setBoard([...initialBoard.map(row => [...row])]);
      setGameOver(false);
      setWrongCells(new Set());
      setSelectedCell(null);
    }
  }, [hasUserInput, initialBoard]);

  useEffect(() => {
    if (lives === 0) {
      setGameOver(true);
      onGameOver();  // 게임오버 모달 표시
    }
  }, [lives, onGameOver]);

  useEffect(() => {
    if (resetBoard) {
      setBoard([...initialBoard.map(row => [...row])]);
      setWrongCells(new Set());
      setSelectedCell(null);
      setMemos(Array(9).fill().map(() => Array(9).fill().map(() => new Set())));
    }
  }, [resetBoard, initialBoard]);

  useEffect(() => {
    // 보드 바깥 클릭 시 선택 해제하는 핸들러
    const handleClickOutside = (event) => {
      // board, numbers, controls 영역을 제외한 곳을 클릭했을 때만 선택 해제
      const boardElement = document.querySelector(`.${styles.board}`);
      const numbersElement = document.querySelector(`.${styles.numbers}`);
      const controlsElement = document.querySelector(`.${styles.controls}`);
      
      if (!boardElement?.contains(event.target) && 
          !numbersElement?.contains(event.target) &&
          !controlsElement?.contains(event.target)) {
        setSelectedCell(null);
        setHoveredNumber(null);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener('mousedown', handleClickOutside);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  const handleCellChange = (row, col, value) => {
    const newValue = value === '' ? 0 : parseInt(value);
    if (isNaN(newValue) || newValue < 0 || newValue > 9) return;

    const newBoard = [...board];
    newBoard[row][col] = newValue;
    setBoard(newBoard);
  };

  const handleCellClick = (row, col) => {
    // 초기 셀이거나 정답을 맞춘 셀은 선택 불가
    if (initialBoard[row][col] !== 0 || board[row][col] === solution[row][col]) {
      setHoveredNumber(board[row][col]);
      return;
    }

    // 이미 선택된 셀을 다시 클릭하면 선택 해제
    if (selectedCell?.row === row && selectedCell?.col === col) {
      setSelectedCell(null);
      return;
    }

    // 빈 셀이나 오답 셀, 메모가 있는 셀 선택 가능
    setSelectedCell({ row, col });
  };

  const handleNumberInput = (number) => {
    if (selectedCell && !gameOver) {
      const { row, col } = selectedCell;
      if (initialBoard[row][col] !== 0 || board[row][col] === solution[row][col]) {
        return;
      }

      if (isMemoMode) {
        // 메모 모드일 때
        const newMemos = JSON.parse(JSON.stringify(
          memos.map(row => row.map(cell => Array.from(cell)))
        ));
        
        if (newMemos[row][col].includes(number)) {
          newMemos[row][col] = newMemos[row][col].filter(n => n !== number);
        } else {
          newMemos[row][col].push(number);
        }
        
        setMemos(newMemos.map(row => 
          row.map(cell => new Set(cell))
        ));
        return;
      }

      // 실제 숫자 입력 시 메모 삭제
      const newMemos = JSON.parse(JSON.stringify(
        memos.map(row => row.map(cell => Array.from(cell)))
      ));
      newMemos[row][col] = [];
      setMemos(newMemos.map(row => 
        row.map(cell => new Set(cell))
      ));

      const newBoard = [...board];
      
      if (solution[row][col] === number) {
        newBoard[row][col] = number;
        setBoard(newBoard);
        onUserInput();

        // 완성된 라인 체크
        const completedSections = checkCompletedSections(newBoard, row, col);
        if (completedSections.length > 0) {
          setCompletedSections(completedSections);
          try { successAudio.currentTime = 0; } catch (_) {}
          successAudio.play().catch(() => {});
          trackedSetTimeout(() => setCompletedSections([]), 2500);
        }

        // 정답이면 wrongCells에서 제거
        const newWrongCells = new Set(wrongCells);
        newWrongCells.delete(`${row}-${col}`);
        setWrongCells(newWrongCells);
        
        // 정답을 입력한 후에는 선택 해제
        setSelectedCell(null);
        
        // 게임 클리어 체크
        if (checkGameClear(newBoard)) {
          trackedSetTimeout(() => {
            onGameClear();
          }, 2500);
        }
      } else {
        if (board[row][col] === number) return;
        
        newBoard[row][col] = number;
        setBoard(newBoard);
        onUserInput();
        onWrongAnswer();
        try { failAudio.currentTime = 0; } catch (_) {}
        failAudio.play().catch(() => {});
        
        // wrongCells 업데이트 및 애니메이션 재적용을 위한 처리
        // 일시 제거 → 10ms 후 재추가하여 애니메이션 재트리거
        setWrongCells(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${row}-${col}`);
          return newSet;
        });
        trackedSetTimeout(() => {
          setWrongCells(current => new Set(current).add(`${row}-${col}`));
        }, 10);
      }
    }
  };

  // 한 줄이 완성되었는지 체크하는 함수
  const isLineComplete = (line) => {
    const numbers = new Set(line);
    return numbers.size === 9 && !numbers.has(0);
  };

  // 완성된 섹션을 체크하는 함수
  const checkCompletedSections = (board, row, col) => {
    const completed = [];
    
    // 가로줄 체크
    if (isLineComplete(board[row])) {
      completed.push(`row-${row}`);
    }
    
    // 세로줄 체크
    const column = board.map(r => r[col]);
    if (isLineComplete(column)) {
      completed.push(`col-${col}`);
    }
    
    // 3x3 박스 체크
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    const box = [];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        box.push(board[boxRow + i][boxCol + j]);
      }
    }
    if (isLineComplete(box)) {
      completed.push(`box-${boxRow}-${boxCol}`);
    }

    return completed;
  };

  const checkGameClear = (currentBoard) => {
    return currentBoard.every((row, i) => 
      row.every((cell, j) => cell === solution[i][j])
    );
  };

  const getCellClassName = (rowIndex, colIndex, cell) => {
    const classNames = [styles.cell];
    
    if (selectedCell?.row === rowIndex && selectedCell?.col === colIndex) {
      classNames.push(styles.selected);
    }
    
    if (initialBoard[rowIndex][colIndex] !== 0) {
      classNames.push(styles.initial);
    }

    // 정답을 맞춘 셀에 대한 스타일 추가
    if (board[rowIndex][colIndex] === solution[rowIndex][colIndex] && 
        initialBoard[rowIndex][colIndex] === 0) {
      classNames.push(styles.correct);
    }
    
    if (wrongCells.has(`${rowIndex}-${colIndex}`)) {
      classNames.push(styles.wrong);
    }
    
    if (hoveredNumber !== null && board[rowIndex][colIndex] === hoveredNumber) {
      classNames.push(styles.highlighted);
    }
    
    // 완성된 줄/열/박스에 대한 클래스 추가
    completedSections.forEach(section => {
      if (section === `row-${rowIndex}`) {
        classNames.push(styles.completedRow);
        classNames.push(styles[`row-${colIndex}`]);
      }
      if (section === `col-${colIndex}`) {
        classNames.push(styles.completedCol);
        classNames.push(styles[`col-${rowIndex}`]);
      }
      if (section.startsWith('box-')) {
        const [_, boxRow, boxCol] = section.split('-');
        const cellInBoxIndex = (rowIndex % 3) * 3 + (colIndex % 3);
        if (Math.floor(rowIndex/3) * 3 === parseInt(boxRow) && Math.floor(colIndex/3) * 3 === parseInt(boxCol)) {
          classNames.push(styles.completedBox);
          classNames.push(styles[`box-${cellInBoxIndex}`]);
        }
      }
    });
    
    return classNames.join(' ');
  };

  // 숫자가 9개 있는지 확인하는 함수
  const checkNumberCompletion = (currentBoard) => {
    const newCompletedNumbers = new Set();
    
    // 1부터 9까지 각 숫자에 대해
    for (let num = 1; num <= 9; num++) {
      let count = 0;
      // 보드 전체를 순회하며 해당 숫자의 개수를 세기
      for (let i = 0; i < 9; i++) {
        for (let j = 0; j < 9; j++) {
          // 현재 셀이 해당 숫자이고, 초기 셀이거나 정답인 경우에만 카운트
          if (currentBoard[i][j] === num && 
              (initialBoard[i][j] === num || currentBoard[i][j] === solution[i][j])) {
            count++;
          }
        }
      }
      // 숫자가 9개 있으면 완성된 숫자 목록에 추가
      if (count === 9) {
        newCompletedNumbers.add(num);
      }
    }
    setCompletedNumbers(newCompletedNumbers);
  };

  // board가 변경될 때마다 완성된 숫자 체크
  useEffect(() => {
    checkNumberCompletion(board);
  }, [board]);

  // 메모 모드 토글 함수
  const toggleMemoMode = () => {
    setIsMemoMode(!isMemoMode);
  };

  // 지우기 함수 수정
  const clearCell = () => {
    if (selectedCell) {
      const { row, col } = selectedCell;
      // 초기 셀이거나 정답인 셀은 지울 수 없음
      if (initialBoard[row][col] !== 0 || board[row][col] === solution[row][col]) {
        return;
      }

      // 메모가 있으면 메모 지우기
      if (memos[row][col].size > 0) {
        const newMemos = JSON.parse(JSON.stringify(
          memos.map(row => row.map(cell => Array.from(cell)))
        ));
        newMemos[row][col] = [];
        setMemos(newMemos.map(row => 
          row.map(cell => new Set(cell))
        ));
      }
      
      // 오답이 있으면 숫자 지우기
      if (board[row][col] !== 0 && board[row][col] !== solution[row][col]) {
        const newBoard = [...board];
        newBoard[row][col] = 0;
        setBoard(newBoard);
        
        // wrongCells에서 제거
        const newWrongCells = new Set(wrongCells);
        newWrongCells.delete(`${row}-${col}`);
        setWrongCells(newWrongCells);
      }

      onUserInput();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <button 
          className={styles.memoButton}
          onClick={clearCell}
        >
          {t('buttons.erase')}
        </button>
        <button 
          className={`${styles.memoButton} ${isMemoMode ? styles.active : ''}`}
          onClick={toggleMemoMode}
        >
          {t('buttons.memo')}
        </button>
      </div>
      <div className={styles.board}>
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className={styles.row}>
            {row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={getCellClassName(rowIndex, colIndex, cell)}
                onClick={() => !gameOver && handleCellClick(rowIndex, colIndex)}
                onMouseLeave={() => {
                  // initial 셀에서 마우스가 벗어날 때 하이라이트 제거
                  if (initialBoard[rowIndex][colIndex] !== 0) {
                    setHoveredNumber(null);
                  }
                }}
              >
                {board[rowIndex][colIndex] !== 0 ? (
                  board[rowIndex][colIndex]
                ) : (
                  <div className={styles.memoContainer}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                      <div key={num} className={styles.memoNumber}>
                        {memos[rowIndex][colIndex].has(num) ? num : '\u00A0'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      {!gameOver && (
        <div className={styles.numbers}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
            <button
              key={number}
              className={`${styles.numberButton} 
                ${selectedCell?.row === null && selectedCell?.col === null && number === hoveredNumber ? styles.selected : ''} 
                ${completedNumbers.has(number) ? styles.completed : ''}`}
              onClick={() => handleNumberInput(number)}
              disabled={completedNumbers.has(number)}
              onMouseEnter={() => setHoveredNumber(number)}
              onMouseLeave={() => setHoveredNumber(null)}
            >
              {number}
              {completedNumbers.has(number) && (
                <svg 
                  className={styles.crownIcon} 
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    fill="currentColor" 
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Sudoku; 